import { useState, type RefObject } from 'react';
import { ChevronRight } from 'lucide-react';
import roomStyles from '../Room.module.css';
import styles from './ContactRoom.module.css';

type ContactChannel = 'email' | 'linkedin' | 'github';
type ContactRoomProps = { sectionRef: RefObject<HTMLElement | null>; active: boolean };

const contactChannels = {
  email: {
    label: 'EMAIL',
    detail: 'START A NEW PROJECT',
    href: 'mailto:martin.andreev92@gmail.com',
  },
  linkedin: {
    label: 'LINKEDIN',
    detail: 'CONNECT ON LINKEDIN',
    href: 'https://www.linkedin.com/in/martin-andreev-a71b9632/',
  },
  github: { label: 'GITHUB', detail: 'EXPLORE MY CODE', href: 'https://github.com/MartinAndreev' },
} as const;

export function ContactRoom({ sectionRef, active }: ContactRoomProps) {
  const [channel, setChannel] = useState<ContactChannel>('email');
  const selectedContact = contactChannels[channel];

  return (
    <section
      ref={sectionRef}
      className={`${roomStyles.room} ${roomStyles.wideRoom} ${styles.room} ${active ? styles.roomActive : ''}`}
      aria-label="Pixel-art rooftop contact room at sunrise"
    >
      <div className={`${roomStyles.scene} ${styles.scene}`}>
        <picture className={`${roomStyles.background} ${styles.background}`}>
          <source media="(max-width: 680px)" srcSet="/assets/v5-room-contact-mobile.png" />
          <source media="(min-width: 681px)" srcSet="/assets/v5-room-contact-ultrawide.png" />
          <img
            src="/assets/v5-room-contact.png"
            alt="Pixel-art rooftop overlooking a city before sunrise"
          />
        </picture>
        <picture
          className={`${roomStyles.background} ${styles.background} ${styles.dawnBackground}`}
        >
          <source media="(max-width: 680px)" srcSet="/assets/v5-room-contact-dawn-mobile.png" />
          <source media="(min-width: 681px)" srcSet="/assets/v5-room-contact-dawn-ultrawide.png" />
          <img src="/assets/v5-room-contact-dawn.png" alt="" />
        </picture>
        <div className={styles.starParticles} aria-hidden="true">
          {Array.from({ length: 4 }, (_, index) => (
            <i key={index} />
          ))}
        </div>
        <div className={styles.contactConsole}>
          <img
            src="/assets/v5-contact-console.png"
            width={1536}
            height={1024}
            alt="Pixel-art rooftop communications terminal"
          />
          <div className={styles.contactScreen} aria-live="polite">
            <span>WORLD 05 COMPLETE</span>
            <h2>THANKS FOR PLAYING</h2>
            <p>LET&apos;S BUILD SOMETHING.</p>
            <nav aria-label="Contact channels">
              {(Object.keys(contactChannels) as ContactChannel[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  className={channel === item ? styles.contactChannelActive : ''}
                  onClick={() => setChannel(item)}
                  aria-pressed={channel === item}
                >
                  {contactChannels[item].label}
                </button>
              ))}
            </nav>
            <a
              href={selectedContact.href}
              target={channel === 'email' ? undefined : '_blank'}
              rel={channel === 'email' ? undefined : 'noreferrer'}
            >
              {selectedContact.detail} <ChevronRight aria-hidden="true" />
            </a>
          </div>
          <a
            className={styles.transmitHotspot}
            href={selectedContact.href}
            target={channel === 'email' ? undefined : '_blank'}
            rel={channel === 'email' ? undefined : 'noreferrer'}
            aria-label={`Transmit: ${selectedContact.detail}`}
          />
        </div>
      </div>
    </section>
  );
}
